const object = (value) => {
  if (typeof value === "string") {
    try {
      return object(JSON.parse(value));
    } catch {
      return {};
    }
  }
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
};
const text = (value) => (value == null ? "" : String(value).trim());
const approved = [
  "APPROVED",
  "AUTHORIZED",
  "SETTLED",
  "COMPLETED",
  "CAPTURED",
  "CAPTURE_PRESENTED",
  "PRESENTED",
  "PAID",
];
const rejected = ["CANCELED", "CANCELLED", "REJECTED", "EXPIRED", "FAILED"];
const known = [
  ...approved,
  ...rejected,
  "CONCILIATED",
  "PENDING_CUSTOMER",
  "PENDING",
  "PROCESS",
  "CREATED",
  "LINK_EXPIRED",
];
const reasonLabels = {
  REJECTED_BY_FRAUD: "Rechazado por fraude",
  FRAUD_RISK: "Riesgo de fraude",
  INSUFFICIENT_FUNDS: "Fondos insuficientes",
  EXPIRED_CARD: "Tarjeta expirada",
  REJECTED: "Rechazado",
  REJECTED_BY_ISSUER: "Rechazado por el banco",
  CANCELED: "Cancelado",
  EXPIRED: "Expirado",
  FAILED: "Pago fallido",
  UNMAPPED_ERROR: "Error no mapeado",
  UNKNOWN_ERROR: "Error desconocido",
  DUPLICATE_TRANSACTION: "Transacción duplicada",
  INSTRUMENT_BLOCKED: "Instrumento bloqueado",
};
export const formatRecaudoDate = (value) => {
  const date = new Date(value);
  if (!value || !Number.isFinite(date.getTime())) return "Sin fecha";
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("es-CO", {
      timeZone: "America/Bogota",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(date)
      .map(({ type, value: part }) => [type, part])
  );
  return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute}`;
};
export const recaudoDay = (value) => {
  const date = new Date(value);
  return value && Number.isFinite(date.getTime())
    ? new Date(date.getTime() - 18000000).toISOString().slice(0, 10)
    : null;
};
export function normalizeRecaudo(item) {
  const metadata = object(item.metadata),
    more = object(item.more),
    link = object(more.link);
  // Invoice createdAt is five hours ahead of the provider timestamp in this API.
  // Use the provider's original UTC instant; the formatter applies Bogotá once.
  const createdAt =
    [metadata.created_at, link.created_at, item.createdAt].find(
      (value) => value && Number.isFinite(Date.parse(value))
    ) || null;
  const paymentEnvelope = object(more.payment);
  const payment = Array.isArray(paymentEnvelope.data)
    ? object(paymentEnvelope.data[0])
    : object(paymentEnvelope.data || more.payment);
  const main = text(item.status).toUpperCase(),
    nested = text(payment.status).toUpperCase();
  // The invoice status is authoritative; payment details may contain an older attempt.
  let status = known.includes(main) ? main : nested || main || "PENDING";
  const linkId = link.id || metadata.id || null;
  const associatedPaymentId =
    payment.id || payment.payment_id || link.data?.payment_id || metadata.data?.payment_id || null;
  const expires = [link.expires_at, metadata.expires_at]
    .map((value) => Date.parse(value))
    .find(Number.isFinite);
  const unpaidLink = Boolean(linkId) && !associatedPaymentId && !Object.keys(payment).length;
  if (
    unpaidLink &&
    (status === "EXPIRED" ||
      (["CREATED", "PROCESS", "PENDING", "PENDING_CUSTOMER"].includes(status) &&
        Number.isFinite(expires) &&
        expires <= Date.now()))
  )
    status = "LINK_EXPIRED";
  const category =
    status === "CONCILIATED"
      ? "conciliated"
      : approved.includes(status)
      ? "approved"
      : rejected.includes(status)
      ? "rejected"
      : status === "LINK_EXPIRED"
      ? "expired"
      : status === "PENDING_CUSTOMER"
      ? "pending_customer"
      : "pending";
  const labels = {
    conciliated: "Conciliado",
    approved: "Aprobado",
    rejected: "Rechazado",
    expired: "Link vencido",
    pending_customer: "En progreso",
    pending: "Pendiente",
  };
  const color = {
    conciliated: "info",
    approved: "success",
    rejected: "error",
    expired: "expired",
    pending_customer: "info",
    pending: "warning",
  };
  const transactions = Array.isArray(payment.transactions) ? payment.transactions : [];
  const reason = text(
    payment.status_detail ||
      transactions.find((t) => rejected.includes(text(t.status).toUpperCase()) && t.status_detail)
        ?.status_detail
  );
  const rejectionReason =
    category === "rejected"
      ? reasonLabels[reason] ||
        (reason && !["SUCCESS", "COMPLETED"].includes(reason) ? reason : "Sin motivo informado")
      : category === "expired"
      ? "Link expiró"
      : category === "pending_customer"
      ? "Pendiente por cliente"
      : null;
  const paymentId = associatedPaymentId;
  const id = paymentId ?? linkId;
  const isLink = !paymentId && Boolean(linkId);
  const amount = Number(
    metadata.data?.amount?.value ??
      link.data?.amount?.value ??
      payment.initial_amount ??
      payment.current_amount ??
      0
  );
  return {
    id: item.id,
    nit: text(item.nit) || "N/A",
    company: text(item.company) || "N/A",
    amount: Number.isFinite(amount) ? amount : 0,
    status,
    statusCategory: category,
    statusText: status === "PAID" ? "Pagado" : labels[category],
    statusColor: color[category],
    statusDetail: reason || null,
    rejectionReason,
    rawRejectionCode: reason || null,
    isLinkPayment: Boolean(linkId),
    hasAssociatedPayment: Boolean(paymentId),
    paymentId: id,
    paymentIdIsLink: isLink,
    paymentIdDisplay: id,
    paymentIdTooltip: id ? `ID de ${isLink ? "enlace" : "pago"}: ${id}` : null,
    linkUrl: link.url || metadata.url || null,
    linkId,
    billNumber: text(item.bill_number ?? metadata.data?.customer?.factura) || "N/A",
    createdAt,
    createdAtFormatted: formatRecaudoDate(createdAt),
    metadataUrl: metadata.url || null,
    rawData: item,
  };
}
export function filterRecaudos(data, { search = "", start = "", end = "", status = "todos" } = {}) {
  const normalize = (value) =>
    text(value)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  const query = normalize(search);
  const categories = {
    aprobados: "approved",
    conciliados: "conciliated",
    rechazados: "rejected",
    pendientes: "pending",
    enprogreso: "pending_customer",
    vencidos: "expired",
  };
  return data.filter((item) => {
    const day = recaudoDay(item.createdAt);
    return (
      (!query ||
        [
          item.id,
          item.nit,
          item.company,
          item.statusText,
          item.status,
          item.amount,
          item.billNumber,
          item.paymentIdDisplay,
          item.rejectionReason,
        ].some((value) => normalize(value).includes(query))) &&
      (!(start || end) || Boolean(day && (!start || day >= start) && (!end || day <= end))) &&
      (!categories[status] || item.statusCategory === categories[status])
    );
  });
}
export const recaudoExportRows = (data) =>
  data.map((item) => ({
    ID: item.id,
    NIT: item.nit,
    Empresa: item.company,
    Valor: item.amount,
    Estado: item.statusText,
    "Estado original": item.status,
    Motivo: item.rejectionReason || "-",
    "ID Pago / Link": item.paymentIdDisplay || "-",
    "Tipo ID": item.paymentId ? (item.paymentIdIsLink ? "Link" : "Pago") : "-",
    "N° Factura": item.billNumber,
    "Fecha creación · Bogotá": formatRecaudoDate(item.createdAt),
  }));
